import { authClient } from "../../../lib/auth-client";
import { buildPublicAppUrl } from "../../../services/apiUrl";
import {
  createAdminApiFailure,
  createAdminApiSuccess,
  normalizeAdminApiError,
} from "../../shared/api/adminApiErrors";
import type {
  AdminAuthResult,
  RequestPasswordResetRequest,
  RequestPasswordResetResponse,
  ResendVerificationEmailRequest,
  ResendVerificationEmailResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  SignInWithEmailRequest,
  SignInWithEmailResponse,
  SignUpWithEmailRequest,
  SignUpWithEmailResponse,
  VerifyEmailCodeRequest,
  VerifyEmailCodeResponse,
} from "./types/adminAuth.api";

function isEmailNotVerifiedError(error: { code?: string | null; message?: string }): boolean {
  return error.code === "EMAIL_NOT_VERIFIED" || /email not verified/i.test(error.message ?? "");
}

function getVerificationCallbackUrl(
  email: string,
  flow: "sign-in" | "sign-up",
): string | undefined {
  const params = new URLSearchParams({
    email,
    flow,
  });

  return buildPublicAppUrl(`/admin/verify-email?${params.toString()}`);
}

function getPostSignInCallbackUrl(): string {
  return buildPublicAppUrl("/admin");
}

export async function signInWithEmail(
  request: SignInWithEmailRequest,
): Promise<AdminAuthResult<SignInWithEmailResponse>> {
  const result = await authClient.signIn.email({
    email: request.email,
    password: request.password,
    callbackURL: getPostSignInCallbackUrl(),
  });

  if (result.error) {
    const error = normalizeAdminApiError(result.error);
    if (isEmailNotVerifiedError(error)) {
      return createAdminApiSuccess({
        nextStep: "verify_email",
      });
    }

    return createAdminApiFailure(error);
  }

  const userId = (result.data as { user?: { id?: string } } | null)?.user?.id;

  return createAdminApiSuccess({
    nextStep: "complete",
    userId: userId ?? undefined,
  });
}

export async function signUpWithEmail(
  request: SignUpWithEmailRequest,
): Promise<AdminAuthResult<SignUpWithEmailResponse>> {
  const result = await authClient.signUp.email({
    email: request.email,
    password: request.password,
    name: request.fullName,
    callbackURL: getVerificationCallbackUrl(request.email, "sign-up"),
  });

  if (result.error) {
    return createAdminApiFailure(normalizeAdminApiError(result.error));
  }

  return createAdminApiSuccess({
    nextStep: "verify_email",
  });
}

export async function requestPasswordReset(
  request: RequestPasswordResetRequest,
): Promise<AdminAuthResult<RequestPasswordResetResponse>> {
  const result = await authClient.requestPasswordReset({
    email: request.email,
    redirectTo: request.redirectTo,
  });

  if (result.error) {
    return createAdminApiFailure(normalizeAdminApiError(result.error));
  }

  return createAdminApiSuccess({
    deliveryState: "accepted",
  });
}

export async function resetPassword(
  request: ResetPasswordRequest,
): Promise<AdminAuthResult<ResetPasswordResponse>> {
  const result = await authClient.resetPassword({
    newPassword: request.newPassword,
    token: request.token,
  });

  if (result.error) {
    return createAdminApiFailure(normalizeAdminApiError(result.error));
  }

  return createAdminApiSuccess({
    passwordUpdated: true,
  });
}

export async function verifyEmailToken(
  request: VerifyEmailCodeRequest,
): Promise<AdminAuthResult<VerifyEmailCodeResponse>> {
  const result = await authClient.verifyEmail({
    query: {
      token: request.token,
    },
  });

  if (result.error) {
    return createAdminApiFailure(normalizeAdminApiError(result.error));
  }

  return createAdminApiSuccess({
    verified: true,
  });
}

export const verifyEmailCode = verifyEmailToken;

export async function resendVerificationEmail(
  request: ResendVerificationEmailRequest,
): Promise<AdminAuthResult<ResendVerificationEmailResponse>> {
  const result = await authClient.sendVerificationEmail({
    email: request.email,
    callbackURL: request.callbackUrl,
  });

  if (result.error) {
    return createAdminApiFailure(normalizeAdminApiError(result.error));
  }

  return createAdminApiSuccess({
    sent: true,
  });
}

export async function signOutAdminAuthSession(): Promise<
  AdminAuthResult<{ signedOut: true }>
> {
  const result = await authClient.signOut();

  if (result?.error) {
    return createAdminApiFailure(normalizeAdminApiError(result.error));
  }

  return createAdminApiSuccess({
    signedOut: true,
  });
}
